/**
 * Operator Effectiveness Dashboard · Sprint 10.
 *
 * Not a leadership KPI screen. Answers one operator-facing question:
 * "How much routine work has the platform handled for me today?"
 *
 * Reads /api/ops-metrics/today — no business logic on the client.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Sparkle, ClipboardText, Truck, Robot, ArrowClockwise,
  Timer, ShieldCheck, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import EmptyState from "@/components/common/EmptyState";

const fmtPct = (v) => v == null ? "—" : `${Math.round(v * 100)}%`;
const fmtInt = (v) => v == null ? "—" : Number(v).toLocaleString();
const fmtMinutes = (v) => {
  if (v == null) return "—";
  const m = Number(v);
  if (m < 60) return `${Math.round(m)} min`;
  const h = m / 60;
  return `${h.toFixed(1)} h`;
};
const fmtDuration = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  const h = s / 3600;
  return `${h.toFixed(1)}h`;
};

export default function OperatorMetrics() {
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [refreshing, setRe] = useState(false);

  const load = useCallback(async () => {
    setLoad((prev) => data ? false : prev);
    setRe(true);
    try {
      const { data: d } = await api.get("/ops-metrics/today");
      setData(d);
    } catch {
      toast.error("Could not load operator metrics.");
    } finally {
      setLoad(false); setRe(false);
    }
  }, [data]);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-6 text-sm text-slate-500" data-testid="ops-metrics-loading">Loading operator metrics…</div>;
  }
  if (!data) {
    return (
      <div className="p-6">
        <EmptyState
          icon={Sparkle}
          title="No metrics available yet"
          why="This page snapshots the automation platform's work today."
          next="Upload a supplier invoice or a freight PDF to start populating today's numbers."
          cta={{ label: "Go to Receiving", to: "/receiving", testid: "ops-metrics-empty-cta" }}
        />
      </div>
    );
  }

  const s = data.supplier_invoices;
  const f = data.freight_invoices;
  const noWork =
    (s?.total || 0) === 0 &&
    (f?.auto_matched || 0) === 0 &&
    (f?.exceptions_today || 0) === 0;

  return (
    <div className="p-6 space-y-6" data-testid="ops-metrics-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">v1.3 · Sprint 10 · Operational Excellence</div>
          <h1 className="font-display text-2xl font-semibold text-slate-900">
            Operator Effectiveness · Today
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            How much routine work the automation platform has handled for you
            since midnight. Every number ties back to a data source you can verify.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          data-testid="ops-metrics-refresh"
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
        >
          <ArrowClockwise size={14} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </header>

      {noWork ? (
        <EmptyState
          icon={Sparkle}
          title="Quiet morning."
          why="No invoices, freight PDFs, or exceptions have been processed since midnight."
          next="Start by uploading a Suncor supplier invoice or an AARAY freight PDF from Receiving."
          cta={{ label: "Go to Receiving", to: "/receiving", testid: "ops-metrics-quiet-cta" }}
        />
      ) : (
        <>
          {/* Headline */}
          <HeadlineCard
            saved={data.estimated_minutes_saved}
            duplicates={data.duplicates_prevented}
          />

          {/* Supplier invoices */}
          <section data-testid="ops-metrics-supplier">
            <SectionHeading
              icon={ClipboardText}
              title="Supplier Invoices"
              subtitle="Uploaded and processed today."
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Processed"          value={fmtInt(s.total)}           testid="metric-supplier-total" />
              <Metric label="Auto-approved"      value={fmtInt(s.auto_approved)}   accent="emerald"
                      testid="metric-supplier-auto" hint={fmtPct(s.auto_approval_rate) + " of today's uploads"} />
              <Metric label="Requiring review"   value={fmtInt(s.requiring_review)} accent="amber"
                      testid="metric-supplier-review" />
              <Metric label="Avg AI confidence"  value={fmtPct(s.average_confidence)}
                      testid="metric-supplier-confidence" />
            </div>
          </section>

          {/* Freight */}
          <section data-testid="ops-metrics-freight">
            <SectionHeading
              icon={Truck}
              title="Freight Invoices"
              subtitle="AARAY freight PDFs processed today."
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Auto-matched"        value={fmtInt(f.auto_matched)}     accent="emerald"
                      testid="metric-freight-auto" hint={fmtPct(f.auto_match_rate) + " match rate today"} />
              <Metric label="Sent to exceptions"  value={fmtInt(f.exceptions_today)} accent="amber"
                      testid="metric-freight-exc-today" />
              <Metric label="Exceptions open"     value={fmtInt(f.exceptions_open)}
                      testid="metric-freight-exc-open" />
              <Metric label="Avg processing time" value={fmtDuration(data.processing_time_seconds?.average)}
                      testid="metric-processing-time"
                      hint={data.processing_time_seconds?.sample_size
                        ? `Across ${data.processing_time_seconds.sample_size} receipt${data.processing_time_seconds.sample_size===1?"":"s"} closed today`
                        : "No receipts fully closed yet"} />
            </div>
          </section>

          {/* Assumptions transparency */}
          <details className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="ops-metrics-assumptions">
            <summary className="cursor-pointer font-semibold">How "minutes saved" is calculated</summary>
            <ul className="mt-2 space-y-1 pl-4 list-disc">
              <li>{data.assumptions.minutes_per_auto_approved_invoice} min per supplier invoice the AI auto-approved</li>
              <li>{data.assumptions.minutes_per_auto_applied_freight_line} min per freight line the parser auto-matched</li>
              <li>{data.assumptions.minutes_per_duplicate_invoice_prevented} min per duplicate invoice caught before it was posted</li>
            </ul>
            <p className="mt-2">These are conservative Coolman baselines — we'll recalibrate once we have operator time-tracking data.</p>
          </details>
        </>
      )}
    </div>
  );
}

function HeadlineCard({ saved, duplicates }) {
  return (
    <section
      data-testid="ops-metrics-headline"
      className="rounded-lg border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5"
    >
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
            <Robot size={14} weight="fill" /> Estimated automation dividend · today
          </div>
          <div className="mt-2 text-5xl font-semibold tabular-nums text-emerald-900"
               data-testid="ops-metrics-minutes-saved">
            {fmtMinutes(saved)}
          </div>
          <div className="text-sm text-emerald-800 mt-1">of operator work handled by the platform.</div>
        </div>
        <div className="text-right">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1 justify-end">
            <ShieldCheck size={14} weight="fill" /> Duplicate invoices prevented
          </div>
          <div className="mt-1 text-3xl font-semibold tabular-nums text-slate-900"
               data-testid="ops-metrics-duplicates">
            {fmtInt(duplicates)}
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeading({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <Icon size={16} className="text-slate-500 self-center" />
      <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      <span className="text-[11px] text-slate-500">{subtitle}</span>
    </div>
  );
}

function Metric({ label, value, hint, accent, testid }) {
  const tone = accent === "emerald"
    ? "border-emerald-200 bg-emerald-50"
    : accent === "amber"
    ? "border-amber-200 bg-amber-50"
    : "border-slate-200 bg-white";
  return (
    <div className={`rounded-md border ${tone} p-3`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-2xl font-semibold tabular-nums text-slate-900 mt-0.5">{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}
